// Copyright 2020 Sorama B.V.

import {
    autoinject,
    bindable,
    bindingMode,
    computedFrom,
    containerless,
    customElement,
    observable,
    TaskQueue,
} from "aurelia-framework";
import { CategoricalColorPalettes } from "services/Color/CategoricalColorPalettes";
import { Color } from "services/Color/Color";
import { NamedColor } from "services/Color/NamedColor";
import tippy, { Instance, Props } from "tippy.js";

@autoinject
@customElement("color-picker")
@containerless
export class ColorPicker {
    private static readonly _basePresets: NamedColor[] = CategoricalColorPalettes.TableauExtendedPortal();
    public get basePresets(): NamedColor[] {
        return ColorPicker._basePresets;
    }

    private static currentlyOpenPicker: ColorPicker;

    @observable
    private presetsElement: HTMLElement;
    private colorElement: HTMLElement;
    private hueSatBoxElement: HTMLElement;
    private brightnessBoxElement: HTMLElement;
    private popupElement: HTMLElement;

    private taskQueue: TaskQueue;

    private tippyPopup: Instance<Props>;

    @computedFrom("hueSatBoxElement.offsetWidth", "hue")
    private get hueSatX(): number {
        return this.hueSatBoxElement.offsetWidth * this.hue;
    }

    @computedFrom("hueSatBoxElement.offsetHeight", "sat")
    private get hueSatY(): number {
        return this.hueSatBoxElement.offsetHeight * (1 - this.sat);
    }

    @computedFrom("brightnessBoxElement.offsetHeight", "brightness")
    private get brightnessY(): number {
        return this.brightnessBoxElement.offsetHeight * (1 - this.brightness);
    }

    @bindable
    public iconTitle: string;

    @bindable({ defaultBindingMode: bindingMode.twoWay })
    public selectedColor: NamedColor;

    @bindable
    public iconElement: HTMLElement;

    private brightness = 1;

    private pickerTabVisible: boolean;
    private showPicker: boolean;

    private hue: number;
    private sat: number;

    constructor(taskQueue: TaskQueue) {
        this.taskQueue = taskQueue;
    }

    public bind(): void {
        this.brightness = 1;
    }

    public unbind(): void {
        this.closePicker();
    }

    public presetsElementChanged(): void {
        this.taskQueue.queueTask(() => {
            if (this.presetsElement) {
                const selected = this.selectedColor;
                if (!selected) return;

                let element: Element = undefined;
                const color = this.basePresets.find((x) => x.color.css == selected.color.css);

                if (!color) {
                    return;
                }

                // Direct child
                element = this.presetsElement.children.namedItem(color.name);

                // Scroll correct color in to view
                if (element) {
                    element.scrollIntoView(true);
                }
            }
        });
    }

    public getPopupX(): number {
        const iconElement = this.iconElement ? this.iconElement : this.colorElement;
        return iconElement.getBoundingClientRect().left - iconElement.offsetParent.getBoundingClientRect().left;
    }

    public getPopupY(): number {
        const iconElement = this.iconElement ? this.iconElement : this.colorElement;
        return iconElement.getBoundingClientRect().top - iconElement.offsetParent.getBoundingClientRect().top;
    }

    public openPicker(event?: Event): void {
        if (event) {
            event.stopPropagation();
            event.cancelBubble = true;
        }

        if (this.showPicker) return;

        this.pickerTabVisible =
            this.selectedColor && !this.basePresets.some((x) => x.color.css == this.selectedColor.color.css);

        const color = this.selectedColor;
        const hsv = color.color.hsv;
        this.hue = hsv.h;
        this.sat = hsv.s;
        this.brightness = hsv.v;

        if (ColorPicker.currentlyOpenPicker) {
            ColorPicker.currentlyOpenPicker.closePicker();
        }
        ColorPicker.currentlyOpenPicker = this;

        this.showPicker = true;

        this.taskQueue.queueTask(() => {
            if (this.popupElement) {
                this.tippyPopup = tippy(this.iconElement ? this.iconElement : this.colorElement, {
                    content: this.popupElement,
                    theme: "color-picker",
                    interactive: true,
                    trigger: "manual",
                    placement: "auto",
                    appendTo: document.body,
                    onHidden: () => {
                        this.closePicker();
                    },
                });

                this.tippyPopup.show();
            }
        });
    }

    public closePicker(): void {
        if (this.tippyPopup) {
            this.tippyPopup.destroy();
            this.tippyPopup = undefined;
        }

        this.showPicker = false;
        ColorPicker.currentlyOpenPicker = undefined;
    }

    public toName(name: string): string {
        return name[0].toUpperCase() + name.substr(1);
    }

    public onColorChange(event: MouseEvent): void {
        if (event.buttons & 1) {
            this.hue = event.offsetX / (<HTMLElement>event.target).offsetWidth;
            this.sat = 1 - event.offsetY / (<HTMLElement>event.target).offsetHeight;

            this.updateColorPickerColor();
        }
    }

    public onBrightnessChange(event: MouseEvent): void {
        if (event.buttons & 1) {
            this.brightness = 1 - event.offsetY / (<HTMLElement>event.target).offsetHeight;
            this.updateColorPickerColor();
        }
    }

    public updateColorPickerColor(): void {
        const rgb = Color.fromHSV(this.hue, this.sat, 1);
        const brightenedRGB = rgb.scale(this.brightness);

        this.selectedColor = new NamedColor("", brightenedRGB);
    }
}
